/**
 * Anonymous device identity via localStorage.
 *
 * Generates a UUID on first visit and persists it as 'aol_device_token'.
 * This token identifies the device for chat history without requiring
 * authentication. When Cognito auth is added, the backend links this
 * token to the user's account via `devices.user_id`.
 *
 * SSR-safe: returns empty string on the server (Next.js SSR/SSG).
 */

import { v4 as uuidv4 } from 'uuid';

const STORAGE_KEY = 'aol_device_token';

// Process-lifetime fallback when localStorage is unavailable (Safari Private
// Browsing, quota exhausted, storage blocked). The token won't survive a reload
// in that case, but getDeviceToken() stays non-throwing — it's called on every
// conversationFetch and inside the WS onopen settings send, so a throw here
// would break session setup, not just history.
let memoryToken: string | null = null;

/**
 * Get or create a persistent device token.
 *
 * Returns a stable UUID that survives page refreshes and browser
 * restarts. Only cleared if the user manually clears localStorage.
 */
export function getDeviceToken(): string {
  if (typeof window === 'undefined') {
    return ''; // SSR — no localStorage available
  }

  try {
    let token = localStorage.getItem(STORAGE_KEY);
    if (!token) {
      token = uuidv4();
      localStorage.setItem(STORAGE_KEY, token);
    }
    return token;
  } catch {
    // localStorage threw (private mode / quota / blocked). Use a stable
    // in-memory token so the session still works for this page lifetime.
    if (!memoryToken) memoryToken = uuidv4();
    return memoryToken;
  }
}
